import { useState, useRef, useCallback } from 'react';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { Flex } from '@/UI/styles';
import GalleryMenu from '@/components/Common/ImageList/GalleryMenu';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useContextMenu } from '@/hooks/useContextMenu';
import { Content } from './MediaGallery/Content';
import { Sidebar } from './MediaGallery/Sidebar';
import type { MediaAsset, TextAsset } from '@/types/api/assets';

interface MediaGalleryProps {
  title: string;
  /** Shown in the params sidebar "Model" row; defaults to `title`. */
  paramsPanelModelName?: string;
  assets: (TextAsset | MediaAsset)[];
  initialSelectedId: string;
  onDelete: (id: string) => void;
  onDeleteOthers: (index: number) => void;
  onDeleteAll: () => void;
  onSetAsCover: () => void;
  onCopyImage?: (id: string) => Promise<void>;
  onDownload?: (index: number) => Promise<void>;
  onDownloadAll?: () => Promise<void>;
  onClose: () => void;
}

function MediaGallery({
  title,
  paramsPanelModelName,
  assets,
  initialSelectedId,
  onDelete,
  onDeleteOthers,
  onDeleteAll,
  onSetAsCover,
  onCopyImage,
  onDownload,
  onDownloadAll,
  onClose,
}: MediaGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(() => assets.findIndex((asset) => asset.id === initialSelectedId));
  const [transformKey, setTransformKey] = useState(0);
  const [scale, setScale] = useState(1);
  const {
    contextMenu,
    rightClickedItem: rightClickedIndex,
    openContextMenu: handleContextMenu,
    closeContextMenu: handleContextMenuClose,
  } = useContextMenu<number>();
  const resetTransformRef = useRef<ReactZoomPanPinchRef>(null);
  const { track } = useAnalytics();

  const selectedAsset = assets[selectedIndex];

  const handleSetSelected = (index: number) => setSelectedIndex(index);

  const handleDownload = async (index: number): Promise<void> => {
    track('gallery_download_clicked', {}, TrackTypeEnum.BI);
    if (onDownload) {
      await onDownload(index);
    }
  };

  const handleDownloadAll = useCallback(async (): Promise<void> => {
    track('gallery_download_all_clicked', {}, TrackTypeEnum.BI);
    if (onDownloadAll) {
      await onDownloadAll();
    }
  }, [onDownloadAll, track]);

  const handleCopyImage = async (index: number): Promise<void> => {
    if (onCopyImage) {
      const file = assets[index];
      if (file) {
        await onCopyImage(file.id);
      }
    }
    handleContextMenuClose();
  };

  const deleteCurrent = () => {
    if (!selectedAsset) {
      return;
    }
    if (selectedIndex === assets.length - 1 && selectedIndex !== 0) {
      setSelectedIndex(selectedIndex - 1);
    }
    onDelete(selectedAsset.id);
  };

  const deleteOthers = () => {
    setSelectedIndex(0);
    onDeleteOthers(selectedIndex);
  };

  const deletionFunctions = {
    deleteCurrentResult: deleteCurrent,
    deleteAllOtherResults: deleteOthers,
    deleteAllResults: onDeleteAll,
  };

  if (!selectedAsset || selectedIndex < 0) {
    return null;
  }

  return (
    <Flex data-testid="media-gallery-container" sx={{ width: '100%', height: '100%', p: 2 }} onClick={onClose}>
      <Content
        nodeName={title}
        paramsPanelNodeName={paramsPanelModelName || title}
        assets={assets}
        selectedIndex={selectedIndex}
        handleDownload={handleDownload}
        handleDownloadAll={handleDownloadAll}
        transformKey={transformKey}
        deletionFunctions={deletionFunctions}
        onContextMenu={handleContextMenu}
        onClose={onClose}
        resetTransformRef={resetTransformRef}
        scale={scale}
        setScale={setScale}
        handleCopyImage={handleCopyImage}
      />
      <Sidebar
        setSelected={handleSetSelected}
        images={assets}
        selected={selectedIndex}
        onContextMenu={handleContextMenu}
        transformKey={transformKey}
        setTransformKey={setTransformKey}
        resetTransformRef={resetTransformRef}
        setScale={setScale}
      />
      <GalleryMenu
        contextMenu={contextMenu}
        handleContextMenuClose={handleContextMenuClose}
        handleDownload={handleDownload}
        handleDownloadAll={handleDownloadAll}
        handleSetAsCover={onSetAsCover}
        selectedFile={selectedAsset}
        rightClickedIndex={rightClickedIndex}
        handleCopyImage={handleCopyImage}
      />
    </Flex>
  );
}

export { MediaGallery };
